import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import {
  Wallet,
  Calendar,
  CheckCircle2,
  AlertCircle,
  GraduationCap,
  BadgeEuro,
  Receipt
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

interface MonthlyPayment {
  paymentId: string | null;
  month: number;
  year: number;
  monthLabel: string;
  amountDue: number;
  amountPaid: number;
  balance: number;
  status: string;
  paymentMethod: string;
  paidAt: string | null;
  reference: string;
}

interface StudentPaymentsResponse {
  year: number;
  class: { _id: string; name: string; level: string; tuition: number } | null;
  monthlyTuition: number;
  summary: {
    totalDue: number;
    totalPaid: number;
    totalBalance: number;
    paidMonths: number;
    partialMonths: number;
    unpaidMonths: number;
  };
  payments: MonthlyPayment[];
}

const STATUS_DEFS: Record<string, { label: string; color: string }> = {
  paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
  partial: { label: 'Partiel', color: 'bg-amber-100 text-amber-800' },
  unpaid: { label: 'Impayé', color: 'bg-red-100 text-red-700' },
  exempted: { label: 'Exonéré', color: 'bg-gray-100 text-gray-700' },
  voided: { label: 'Annulé', color: 'bg-gray-100 text-gray-500' }
};

const METHOD_LABELS: Record<string, string> = {
  cash: 'Espèces',
  bank_transfer: 'Virement bancaire',
  mobile_money: 'Mobile Money',
  check: 'Chèque',
  other: 'Autre'
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' €';

const StudentPayments: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState<StudentPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - 1 + i);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/payments/mine?year=${year}`);
        setData(response.data);
      } catch (error: any) {
        console.error('Error loading student payments:', error);
        toast.error(error.response?.data?.message || 'Erreur lors du chargement de vos paiements');
      } finally {
        setLoading(false);
      }
    };
    loadPayments();
  }, [year]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mes Frais de Scolarité</h1>
            <p className="text-gray-500 mt-1">Relevé mensuel de vos paiements</p>
          </div>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Class info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center gap-4">
          <div className="p-3 bg-brand-100 rounded-lg">
            <GraduationCap className="h-8 w-8 text-brand-600" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">Ma classe</p>
            <p className="text-xl font-bold text-gray-900">
              {data?.class ? `${data.class.name} (${data.class.level})` : 'Non affecté'}
            </p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-gray-500 text-sm">Frais mensuels</p>
            <p className="text-xl font-bold text-brand-600">{formatCurrency(data?.monthlyTuition ?? 0)}/mois</p>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Wallet className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total payé</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(data?.summary.totalPaid ?? 0)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Calendar className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Mois payés</p>
                <p className="text-2xl font-bold text-gray-900">{data?.summary.paidMonths ?? 0}<span className="text-gray-400 text-lg">/12</span></p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BadgeEuro className="h-6 w-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Mois partiels</p>
                <p className="text-2xl font-bold text-gray-900">{data?.summary.partialMonths ?? 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Reste à payer</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(data?.summary.totalBalance ?? 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center">
            <Receipt className="h-5 w-5 text-brand-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Relevé mensuel {year}</h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto" />
              <p className="text-gray-500 mt-4">Chargement de votre relevé...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mois</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant dû</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reste</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Méthode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.payments.map((p) => {
                    const status = STATUS_DEFS[p.status] || STATUS_DEFS.unpaid;
                    const isFuture = p.month > now.getMonth() + 1 && p.year === now.getFullYear();
                    return (
                      <tr key={p.month} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {p.status === 'paid' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                            ) : p.status === 'unpaid' && !isFuture ? (
                              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                            ) : (
                              <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <span className="text-sm font-medium text-gray-900">{p.monthLabel}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(p.amountDue)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-700">
                          {p.amountPaid > 0 ? formatCurrency(p.amountPaid) : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                          {p.balance > 0 ? formatCurrency(p.balance) : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                            {status.label}
                            {isFuture && p.status === 'unpaid' ? ' (à venir)' : ''}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {p.paymentMethod ? METHOD_LABELS[p.paymentMethod] || p.paymentMethod : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {p.paidAt ? new Date(p.paidAt).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {p.reference || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Les paiements sont à régler auprès du secrétariat de l'école. Conservez votre reçu de paiement.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const XCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default StudentPayments;